import { useState, useEffect } from 'react';
import type { IAsset } from '../providers';
import { captionChangedBody, type SystemMessage } from '../echo/systemMessages';
import type { PostSystemCommentOptions } from '../echo/EchoContext';

export interface UseAssetNameEditResult {
  editedName: string;
  isLoadingName: boolean;
  handleNameChange: (newName: string) => void;
  handleSaveName: () => Promise<void>;
  isSuccess: boolean;
  error: string | null;
}

/**
 * Hook for managing asset name editing with auto-save on blur
 * Handles state management and API calls for renaming assets
 *
 * @param asset - The asset to edit (can be null)
 * @returns Object containing state and handler functions
 */
export function useAssetNameEdit(
  asset: IAsset | null,
  postSystemComment?: (assetId: string, message: SystemMessage, options?: PostSystemCommentOptions) => void,
): UseAssetNameEditResult {
  const [editedName, setEditedName] = useState<string>('');
  const [isLoadingName, setIsLoadingName] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize edited name when asset changes
  useEffect(() => {
    if (!asset) {
      setEditedName('');
      return;
    }
    setEditedName(asset.getName());
  }, [asset]);

  const handleNameChange = (newName: string) => {
    setEditedName(newName);
  };

  const handleSaveName = async () => {
    if (!asset || !editedName.trim()) return;

    const originalName = asset.getName();
    if (editedName === originalName) return; // No change

    setIsLoadingName(true);
    try {
      await asset.setName(editedName);
      setIsSuccess(true);
      setError(null);
      postSystemComment?.(asset.getId(), captionChangedBody(originalName, editedName), {
        coalesceKey: 'caption',
        rewriteBody: (prior) => {
          // Preserve the very first "from" name across an A→B→C burst:
          // the prior message's `data.originalFrom` is A, so the new body
          // becomes 'renamed "A" to "C"' instead of 'renamed "B" to "C"'.
          const priorOriginal = (prior.data?.originalFrom as string | undefined) ?? originalName;
          // Net no-op: burst ended at the original name (A→B→A).
          if (priorOriginal === editedName) return null;
          return captionChangedBody(priorOriginal, editedName);
        },
      });
    } catch (error) {
      console.error('Failed to update asset name:', error);
      setEditedName(originalName); // Revert on error
      setIsSuccess(false);
      setError('Failed to update asset name');
    } finally {
      setIsLoadingName(false);
    }
  };

  return {
    editedName,
    isLoadingName,
    handleNameChange,
    handleSaveName,
    isSuccess,
    error,
  };
}
